import { useEffect, useState } from 'react';
import { Button, Modal } from 'react-bootstrap';
import MentorshipService, {
    MENTEE_POOL_ALREADY_IN_POOL,
    MENTEE_POOL_UNKNOWN_STUDENT,
    MENTEE_POOL_UNKNOWN_STUDENT_CODE,
    MentorshipCampusOption,
    MentorshipMenteePoolSearchCandidate,
    MentorshipTaxonomyTrack,
} from './MentorshipService';

interface MentorshipMenteePoolManualAddProps {
    show: boolean;
    cohortId: number | null;
    disabled?: boolean;
    campuses: MentorshipCampusOption[];
    tracks: MentorshipTaxonomyTrack[];
    onClose: () => void;
    onAdded: () => void;
}

const MentorshipMenteePoolManualAdd = ({
    show,
    cohortId,
    disabled,
    campuses,
    tracks,
    onClose,
    onAdded,
}: MentorshipMenteePoolManualAddProps) => {
    const [search, setSearch] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [infoMessage, setInfoMessage] = useState('');
    const [candidates, setCandidates] = useState<MentorshipMenteePoolSearchCandidate[]>([]);
    const [selected, setSelected] = useState<MentorshipMenteePoolSearchCandidate | null>(null);
    const [campusId, setCampusId] = useState<number | null>(null);
    const [trackIds, setTrackIds] = useState<number[]>([]);

    useEffect(() => {
        if (!show) return;
        setSearch('');
        setCandidates([]);
        setSelected(null);
        setCampusId(campuses.length === 1 ? campuses[0].value : null);
        setTrackIds([]);
        setErrorMessage('');
        setInfoMessage('');
    }, [show, campuses]);

    const campusChoices = (candidate: MentorshipMenteePoolSearchCandidate | null) => {
        if (!candidate) return campuses;
        const ids = new Set(candidate.campuses.map((item) => item.campusId));
        const matched = campuses.filter((campus) => ids.has(campus.value));
        return matched.length > 0 ? matched : campuses;
    };

    const runSearch = async () => {
        if (cohortId == null || !search.trim()) return;
        setIsSearching(true);
        setErrorMessage('');
        setInfoMessage('');
        setSelected(null);
        try {
            const rows = await MentorshipService.searchMenteePoolManualAddCandidates(cohortId, search.trim());
            // Controller override of DEC-P4-007: do not list students already in this cohort pool.
            const available = rows.filter((item) => !item.alreadyInPool);
            setCandidates(available);
            if (available.length === 0) {
                setInfoMessage(
                    rows.length > 0 ? MENTEE_POOL_ALREADY_IN_POOL : MENTEE_POOL_UNKNOWN_STUDENT
                );
            }
        } catch (error) {
            setCandidates([]);
            const message = MentorshipService.getErrorMessage(error);
            setErrorMessage(
                message === MENTEE_POOL_UNKNOWN_STUDENT_CODE ? MENTEE_POOL_UNKNOWN_STUDENT : message
            );
        } finally {
            setIsSearching(false);
        }
    };

    const choose = (candidate: MentorshipMenteePoolSearchCandidate) => {
        if (candidate.alreadyInPool) return;
        setSelected(candidate);
        const choices = campusChoices(candidate);
        const preferred =
            candidate.campuses[0]?.campusId ?? (choices.length === 1 ? choices[0].value : null);
        setCampusId(preferred);
        setErrorMessage('');
    };

    const submit = async () => {
        if (cohortId == null || !selected || selected.alreadyInPool) return;
        if (campusId == null) {
            setErrorMessage('Campus is required.');
            return;
        }
        if (trackIds.length < 1) {
            setErrorMessage('Select at least one career track.');
            return;
        }
        setIsSaving(true);
        setErrorMessage('');
        try {
            await MentorshipService.addMenteePoolMember(cohortId, {
                studentId: selected.studentId,
                campusId,
                trackIds,
            });
            onAdded();
            onClose();
        } catch (error) {
            const message = MentorshipService.getErrorMessage(error);
            setErrorMessage(
                message === MENTEE_POOL_UNKNOWN_STUDENT_CODE ? MENTEE_POOL_UNKNOWN_STUDENT : message
            );
        } finally {
            setIsSaving(false);
        }
    };

    const selectedCampuses = campusChoices(selected);
    const selectedCampusProgram =
        selected?.campuses.find((item) => item.campusId === campusId)?.programName ||
        selected?.campuses[0]?.programName ||
        null;

    const candidateProgramLabel = (item: MentorshipMenteePoolSearchCandidate) => {
        const names = item.campuses
            .map((campus: { programName?: string | null; program_name?: string | null }) => {
                const raw = campus.programName ?? campus.program_name ?? null;
                return raw == null ? '' : String(raw).trim();
            })
            .filter((name) => name !== '');
        return Array.from(new Set(names)).join(', ');
    };

    const candidateResultLabel = (item: MentorshipMenteePoolSearchCandidate) => {
        const parts = [item.name || '—', item.uuid || ''].filter((part) => String(part).trim() !== '');
        const program = candidateProgramLabel(item);
        if (program) parts.push(program);
        return parts.join(' · ');
    };

    return (
        <Modal show={show} onHide={onClose} size="lg" centered>
            <Modal.Header closeButton>
                <Modal.Title>Manual Add</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
                {infoMessage && <div className="alert alert-info">{infoMessage}</div>}
                <div className="input-group mb-3">
                    <input
                        className="form-control"
                        placeholder="Search name or UUID"
                        value={search}
                        disabled={disabled}
                        onChange={(event) => setSearch(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault();
                                runSearch();
                            }
                        }}
                    />
                    <button type="button" className="btn btn-outline-secondary" disabled={disabled || isSearching} onClick={runSearch}>
                        Search
                    </button>
                </div>
                {candidates.length > 0 && (
                    <div className="list-group mb-3" style={{ maxHeight: 240, overflowY: 'scroll' }}>
                        {candidates.map((item) => (
                            <button
                                type="button"
                                key={item.studentId}
                                className={`list-group-item list-group-item-action ${
                                    selected?.studentId === item.studentId ? 'active' : ''
                                }`}
                                onClick={() => choose(item)}>
                                {candidateResultLabel(item)}
                            </button>
                        ))}
                    </div>
                )}
                {selected && !selected.alreadyInPool && (
                    <div className="row g-3">
                        <div className="col-md-6">
                            <label className="form-label">Name</label>
                            <input className="form-control" value={selected.name || ''} readOnly />
                        </div>
                        {/* UUID field removed (QA): not useful to the admin in this form. The uuid is
                            still shown in the search results and still submitted with the payload. */}
                        <div className="col-md-6">
                            <label className="form-label">Campus</label>
                            <select
                                className="form-select"
                                value={campusId ?? ''}
                                onChange={(event) =>
                                    setCampusId(event.target.value ? Number(event.target.value) : null)
                                }>
                                <option value="">Select campus</option>
                                {selectedCampuses.map((campus) => (
                                    <option key={campus.value} value={campus.value}>
                                        {campus.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Program</label>
                            <input className="form-control" value={selectedCampusProgram || ''} readOnly />
                        </div>
                        <div className="col-12">
                            <label className="form-label">Career Tracks</label>
                            <div>
                                {tracks
                                    .filter((track) => track.id != null)
                                    .map((track) => (
                                        <label key={track.id} className="me-3">
                                            <input
                                                type="checkbox"
                                                className="me-1"
                                                checked={trackIds.includes(track.id as number)}
                                                onChange={() =>
                                                    setTrackIds((current) =>
                                                        current.includes(track.id as number)
                                                            ? current.filter((id) => id !== track.id)
                                                            : [...current, track.id as number]
                                                    )
                                                }
                                            />
                                            {track.name}
                                        </label>
                                    ))}
                            </div>
                        </div>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={onClose}>
                    Close
                </Button>
                <Button
                    variant="success"
                    disabled={disabled || isSaving || !selected || selected.alreadyInPool}
                    onClick={submit}>
                    Add to Mentee Pool
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default MentorshipMenteePoolManualAdd;
